import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { usePromptPreferencesBase } from "@/hooks/use-prompt-preferences-base";
import { useApi } from "@/hooks/useApi";
import {
  notifyPinnedChanged,
  PinnedChangedEvent,
  PROMPT_EVENTS,
} from "@/lib/prompt-events";
import { getPromptId } from "@/lib/prompt-utils";
import {
  PinnedContext,
  PromptDTO,
  ReorderPinnedPromptsDTO,
  TogglePinnedPromptDTO,
  UserPromptsPreferencesDTO,
} from "@shaarp/common";
import { useCallback, useEffect, useRef, useState } from "react";

type UsePinnedPromptsOptions = {
  context?: PinnedContext;
  fetchCustomPrompts?: boolean;
  listenForChanges?: boolean;
};

type UsePinnedPromptsReturn = {
  pinnedIds: Set<string>;
  pinnedPrompts: PromptDTO[];
  customPrompts: PromptDTO[];
  togglePinned: (prompt: PromptDTO) => Promise<boolean>;
  reorderPinned: (orderedPrompts: PromptDTO[]) => Promise<void>;
  isPinned: (prompt: PromptDTO) => boolean;
  isLimitReached: boolean;
  refreshPinned: () => Promise<void>;
  isLoading: boolean;
};

export function usePinnedPrompts(
  options: UsePinnedPromptsOptions = {},
): UsePinnedPromptsReturn {
  const {
    context = "chat",
    fetchCustomPrompts = false,
    listenForChanges = true,
  } = options;
  const api = useApi();
  // useApi() returns a fresh axios instance each render — keep a ref to a stable
  // reference so direct api calls in this hook don't cause effect loops.
  const apiRef = useRef(api);
  apiRef.current = api;
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [pinnedPrompts, setPinnedPrompts] = useState<PromptDTO[]>([]);

  const onPreferencesFetched = useCallback(
    (data: UserPromptsPreferencesDTO): void => {
      setPinnedIds(new Set(data.pinnedPromptIds[context] ?? []));
    },
    [context],
  );

  const { customPrompts, isLoadingPreferences, isLoadingPrompts, fetchPreferences } =
    usePromptPreferencesBase({
      fetchCustomPrompts,
      listenForChanges,
      onPreferencesFetched,
    });

  const isLoading = isLoadingPreferences || isLoadingPrompts;

  const computePinnedPrompts = useCallback(
    (ids: Set<string>, prompts: PromptDTO[]): PromptDTO[] => {
      const promptMap = new Map(prompts.map((p) => [getPromptId(p), p]));
      return [...ids]
        .map((id) => promptMap.get(id))
        .filter((p): p is PromptDTO => !!p);
    },
    [],
  );

  // Update pinned prompts when pinnedIds or customPrompts change
  useEffect(() => {
    setPinnedPrompts(computePinnedPrompts(pinnedIds, customPrompts));
  }, [pinnedIds, customPrompts, computePinnedPrompts]);

  // Listen for pinned changes from other components
  useEffect(() => {
    if (!listenForChanges) return;

    const handlePinnedChanged = (event: PinnedChangedEvent) => {
      if (event.detail.context !== context) return;
      setPinnedIds(new Set(event.detail.pinnedIds));
    };

    window.addEventListener(
      PROMPT_EVENTS.PINNED_CHANGED,
      handlePinnedChanged as EventListener,
    );
    return () => {
      window.removeEventListener(
        PROMPT_EVENTS.PINNED_CHANGED,
        handlePinnedChanged as EventListener,
      );
    };
  }, [listenForChanges, context]);

  const refreshPinned = useCallback(async () => {
    await fetchPreferences();
  }, [fetchPreferences]);

  const isPinned = useCallback(
    (prompt: PromptDTO): boolean => {
      return pinnedIds.has(getPromptId(prompt));
    },
    [pinnedIds],
  );

  const maxPinned = MAX_PINNED_PROMPTS[context];

  const togglePinned = useCallback(
    async (prompt: PromptDTO): Promise<boolean> => {
      const promptId = getPromptId(prompt);

      if (!pinnedIds.has(promptId) && pinnedIds.size >= maxPinned) {
        return false;
      }

      try {
        const { data } = await apiRef.current.post<TogglePinnedPromptDTO>(
          `/prompts/pinned/${promptId}?context=${context}`,
        );

        const newIds = new Set(pinnedIds);
        if (data.isPinned) {
          newIds.add(promptId);
        } else {
          newIds.delete(promptId);
        }

        setPinnedIds(newIds);
        notifyPinnedChanged([...newIds], context);

        return data.isPinned;
      } catch {
        return false;
      }
    },
    [pinnedIds, maxPinned, context],
  );

  const reorderPinned = useCallback(
    async (orderedPrompts: PromptDTO[]): Promise<void> => {
      const orderedIds = orderedPrompts.map((p) => getPromptId(p));
      const previousPinnedPrompts = pinnedPrompts;
      const previousPinnedIds = pinnedIds;

      // Optimistic update
      setPinnedPrompts(orderedPrompts);
      setPinnedIds(new Set(orderedIds));

      try {
        const body: ReorderPinnedPromptsDTO = {
          orderedPromptIds: orderedIds,
          context,
        };
        await apiRef.current.put("/prompts/pinned/reorder", body);

        notifyPinnedChanged(orderedIds, context);
      } catch {
        // Rollback on failure
        setPinnedPrompts(previousPinnedPrompts);
        setPinnedIds(previousPinnedIds);
      }
    },
    [context, pinnedPrompts, pinnedIds],
  );

  return {
    pinnedIds,
    pinnedPrompts,
    customPrompts,
    togglePinned,
    reorderPinned,
    isPinned,
    isLimitReached: pinnedIds.size >= maxPinned,
    refreshPinned,
    isLoading,
  };
}
